"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import type { AccommodationForm } from "./useCreateAccommodationForm";

type AccommodationFormStatus = "DRAFT" | "SUBMITTED" | "SIGNED";

interface AccommodationFormFilterInput {
  status?: AccommodationFormStatus;
}

interface AccommodationFormConnection {
  items: AccommodationForm[];
  nextToken?: string | null;
}

interface GetAccommodationFormsByPatientQueryVariables {
  patientId: string;
  filter?: AccommodationFormFilterInput;
  limit?: number;
  nextToken?: string;
}

export const getAccommodationFormsByPatientQuery = gql`
  query getAccommodationFormsByPatient(
    $patientId: ID!
    $filter: AccommodationFormFilterInput
    $limit: Int
    $nextToken: String
  ) {
    getAccommodationFormsByPatient(
      patientId: $patientId
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        patientId
        episodeOfCareId
        status
        formData
        createdAt
        updatedAt
        submittedAt
        signedAt
        createdBy
      }
      nextToken
    }
  }
`.loc?.source.body as GeneratedQuery<
  GetAccommodationFormsByPatientQueryVariables,
  {
    getAccommodationFormsByPatient: AccommodationFormConnection;
  }
>;

interface UseGetAccommodationFormsByPatientOptions {
  patientId: string;
  filter?: AccommodationFormFilterInput;
  limit?: number;
  enabled?: boolean;
}

export const useGetAccommodationFormsByPatient = ({
  patientId,
  filter,
  limit,
  enabled = true,
}: UseGetAccommodationFormsByPatientOptions) => {
  return useQuery({
    queryKey: ["accommodationForms", patientId, filter, limit],
    queryFn: () =>
      fetchGraphQLClient(getAccommodationFormsByPatientQuery, {
        patientId,
        filter,
        limit,
      }),
    select: (data) =>
      data.data?.getAccommodationFormsByPatient ?? { items: [], nextToken: null },
    enabled: !!patientId && enabled,
  });
};
